import { geoCentroid } from 'd3-geo';
import { getEnumValue } from 'lib/util/enum';
import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Annotation, ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import ReactTooltip from 'react-tooltip';
import { classnames } from 'tailwindcss-classnames';
import { CenterMapProps, ParticipationCenterMapEntry } from './_types';
import { logError, makeLoggableError } from 'lib/util/errors';

const geoUrl = '/static/states-10m.json';
type OffsetType = {
  [name: string]: number[];
};
const offsets: OffsetType = {
  VT: [50, -8],
  NH: [34, 2],
  MA: [30, -1],
  RI: [28, 2],
  CT: [35, 10],
  NJ: [34, 1],
  DE: [33, 0],
  MD: [47, 10],
  DC: [49, 21],
};

const allStates = [
  { id: 'AL', val: '01' },
  { id: 'AK', val: '02' },
  { id: 'AS', val: '60' },
  { id: 'AZ', val: '04' },
  { id: 'AR', val: '05' },
  { id: 'CA', val: '06' },
  { id: 'CO', val: '08' },
  { id: 'CT', val: '09' },
  { id: 'DE', val: '10' },
  { id: 'DC', val: '11' },
  { id: 'FL', val: '12' },
  { id: 'FM', val: '64' },
  { id: 'GA', val: '13' },
  { id: 'GU', val: '66' },
  { id: 'HI', val: '15' },
  { id: 'ID', val: '16' },
  { id: 'IL', val: '17' },
  { id: 'IN', val: '18' },
  { id: 'IA', val: '19' },
  { id: 'KS', val: '20' },
  { id: 'KY', val: '21' },
  { id: 'LA', val: '22' },
  { id: 'ME', val: '23' },
  { id: 'MH', val: '68' },
  { id: 'MD', val: '24' },
  { id: 'MA', val: '25' },
  { id: 'MI', val: '26' },
  { id: 'MN', val: '27' },
  { id: 'MS', val: '28' },
  { id: 'MO', val: '29' },
  { id: 'MT', val: '30' },
  { id: 'NE', val: '31' },
  { id: 'NV', val: '32' },
  { id: 'NH', val: '33' },
  { id: 'NJ', val: '34' },
  { id: 'NM', val: '35' },
  { id: 'NY', val: '36' },
  { id: 'NC', val: '37' },
  { id: 'ND', val: '38' },
  { id: 'MP', val: '69' },
  { id: 'OH', val: '39' },
  { id: 'OK', val: '40' },
  { id: 'OR', val: '41' },
  { id: 'PW', val: '70' },
  { id: 'PA', val: '42' },
  { id: 'PR', val: '72' },
  { id: 'RI', val: '44' },
  { id: 'SC', val: '45' },
  { id: 'SD', val: '46' },
  { id: 'TN', val: '47' },
  { id: 'TX', val: '48' },
  { id: 'UM', val: '74' },
  { id: 'UT', val: '49' },
  { id: 'VT', val: '50' },
  { id: 'VA', val: '51' },
  { id: 'VI', val: '78' },
  { id: 'WA', val: '53' },
  { id: 'WV', val: '54' },
  { id: 'WI', val: '55' },
  { id: 'WY', val: '56' },
];
const centerTypeClass = classnames('p-1', 'border', 'border-gray-manatee', 'ml-1');
const CenterMapComponent = ({ mapfields }: CenterMapProps): JSX.Element => {
  const allValidCenter = mapfields?.root?.children?.filter((entry) => {
    return entry.fields?.latitude.value !== null || entry.fields?.longitude.value !== null;
  }) as ParticipationCenterMapEntry[];
  const [tooltipContent, setContent] = useState({
    centerId: '',
    groupNumber: '',
  });
  const hasHPPColumn = mapfields?.root?.fields?.selectColumns.some(
    (p) => getEnumValue(p) === 'hpphospital'
  );
  const hasNDMSColumn = mapfields?.root?.fields?.selectColumns.some(
    (p) => getEnumValue(p) === 'ndmshospital'
  );

  const hasBurnColumn = mapfields?.root?.fields?.selectColumns.some(
    (p) => getEnumValue(p) === 'burncenter'
  );

  const hasTraumaColumn = mapfields?.root?.fields?.selectColumns.some(
    (p) => getEnumValue(p) === 'traumacenter'
  );
  const hasAdultColumn = mapfields?.root?.fields?.selectColumns.some(
    (p) => getEnumValue(p) === 'adultpediatric'
  );

  const toolContent = (): JSX.Element => {
    if (tooltipContent.centerId === '') {
      return <></>;
    }
    const centersToShow = () => {
      if (tooltipContent.groupNumber !== '') {
        return allValidCenter.filter((entry) => {
          return (
            entry.fields?.groupNumber.value.toLowerCase() ===
            tooltipContent.groupNumber.toLowerCase()
          );
        });
      }
      return allValidCenter.filter((entry) => {
        return entry.id === tooltipContent.centerId;
      });
    };
    const individualCenters = centersToShow().map((item) => {
      return (
        <div key={item.id}>
          <div className={classnames('text-left', 'font-bold')}>
            {item.fields?.displayName.value}
          </div>
          <div className={classnames('text-left')}>{item.fields?.city.value}</div>
          <div className={classnames('pb-2', 'pt-2', 'border-b', 'border-gray-whisper')}>
            {getEnumValue(item.fields?.adultPediatric) !== '' && hasAdultColumn && (
              <span className={centerTypeClass}>{getEnumValue(item.fields?.adultPediatric)}</span>
            )}
            {getEnumValue(item.fields?.traumaCenter) !== 'N/A' && hasTraumaColumn && (
              <span className={centerTypeClass}>{getEnumValue(item.fields?.traumaCenter)}</span>
            )}
            {getEnumValue(item.fields?.burnCenter) === 'Yes' && hasBurnColumn && (
              <span className={centerTypeClass}>Burn Center</span>
            )}
            {getEnumValue(item.fields?.ndmsHospital) === 'Yes' && hasNDMSColumn && (
              <span className={centerTypeClass}>NDMS</span>
            )}
            {getEnumValue(item.fields?.hppHospital) === 'Yes' && hasHPPColumn && (
              <span className={centerTypeClass}>HPP</span>
            )}
          </div>
        </div>
      );
    });
    return (
      <div className={classnames('min-w-[442.5px]', 'p-2')}>
        <div
          className={classnames(
            'text-left',
            'text-xl',
            'text-blue-navy',
            'border-b-2',
            'border-gray-whisper',
            'pb-2',
            'font-bold'
          )}
        >
          {centersToShow()[0].fields?.state?.name}
        </div>
        <div>{individualCenters}</div>
      </div>
    );
  };

  return (
    <div>
      <div className={classnames('hidden', 'sm:block')}>
        <ReactTooltip type="light" border className="mapToolTip" borderColor="blue">
          {toolContent()}
        </ReactTooltip>
      </div>
      <div>
        <ComposableMap projection="geoAlbersUsa" onMouseDown={(e) => e.preventDefault()}>
          <Geographies geography={geoUrl}>
            {({ geographies }) => (
              <>
                {geographies.map((geo) => (
                  <Geography key={geo.rsmKey} stroke="#FFF" geography={geo} fill="#9ACA3B" />
                ))}
                {geographies.map((geo) => {
                  const centroid = geoCentroid(geo);
                  const cur = allStates.find((s) => s.val === geo.id);
                  return (
                    <g key={`${geo.rsmKey}-name`}>
                      {cur &&
                        centroid[0] > -160 &&
                        centroid[0] < -67 &&
                        (Object.keys(offsets).indexOf(cur.id) === -1 ? (
                          <Marker coordinates={centroid}>
                            <text y="2" fontSize={14} textAnchor="middle">
                              {cur.id}
                            </text>
                          </Marker>
                        ) : (
                          <Annotation
                            subject={centroid}
                            dx={offsets[cur.id][0]}
                            dy={offsets[cur.id][1]}
                            connectorProps={{
                              strokeWidth: 1,
                            }}
                          >
                            <text x={4} fontSize={14} alignmentBaseline="middle">
                              {cur?.id}
                            </text>
                          </Annotation>
                        ))}
                    </g>
                  );
                })}
              </>
            )}
          </Geographies>
          {allValidCenter.map((center) => {
            let latitude = center.fields?.latitude.value as number;
            let longitude = center.fields?.longitude.value as number;
            let centerId = center.id;
            const groupNumber = center.fields?.groupNumber.value as string;
            let centerCounts = 1;

            if (groupNumber !== '') {
              const groupedCenters = allValidCenter.filter((entry) => {
                return (
                  entry.fields?.groupNumber.value.toLowerCase() ===
                  center.fields?.groupNumber.value.toLowerCase()
                );
              });
              centerCounts = groupedCenters.length;
              if (centerCounts > 1) {
                latitude = groupedCenters[0].fields?.latitude.value as number;
                longitude = groupedCenters[0].fields?.longitude.value as number;
                centerId = groupedCenters[0].id;
              }
            }

            return (
              <ErrorBoundary
                // eslint-disable-next-line react/no-unstable-nested-components
                fallbackRender={() => {
                  logError(
                    `center-map-component`,
                    makeLoggableError({
                      error: new Error(
                        `${center.fields?.displayName.value} do not have valid coordinates`
                      ),
                    })
                  );
                  return <></>;
                }}
                key={center.id}
              >
                <Marker
                  key={center.id}
                  coordinates={[longitude, latitude]}
                  data-tip=""
                  onMouseEnter={() => {
                    setContent({
                      ...tooltipContent,
                      groupNumber: groupNumber,
                      centerId: centerId,
                    });
                  }}
                  onMouseLeave={() => {
                    setContent({
                      ...tooltipContent,
                      groupNumber: '',
                      centerId: '',
                    });
                  }}
                >
                  <circle r={10} fill="#55565B" stroke="#fff" />
                  <text textAnchor="middle" fill="white" fontSize="15px" dy=".3em">
                    {centerCounts}
                  </text>
                </Marker>
              </ErrorBoundary>
            );
          })}
        </ComposableMap>
      </div>
    </div>
  );
};

export default CenterMapComponent;
